import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Chip, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { format } from 'date-fns';
import { StaffPerformanceMeta, StaffPeriods } from '../types';
import { periodLabel } from '../utils/metrics';

export interface StaffPerformanceHeaderProps {
  subtitle: string;
  periods: StaffPeriods | null;
  meta: StaffPerformanceMeta | null;
  /** Right-aligned control, e.g. the "My performance" button. */
  action?: React.ReactNode;
  backTo?: { to: string; label: string };
  /** When the client-update rollups were last computed successfully; omitted until they are available. */
  updatesCheckedAt?: Date | null;
  /** The latest client-update check failed, so the figures are from an earlier run. */
  updatesFailed?: boolean;
}

/** Page title, subtitle, the two period chips, the last-synced chip and, when available, the client-updates chips. */
export const StaffPerformanceHeader: React.FC<StaffPerformanceHeaderProps> = ({
  subtitle,
  periods,
  meta,
  action,
  backTo,
  updatesCheckedAt,
  updatesFailed,
}) => {
  // Date the data from the last run that wrote documents; a failed attempt must not move it.
  const updatedAt = meta?.last_success_at ?? (meta?.status === 'ok' ? meta.synced_at : null);
  const updated = updatedAt ? `Updated ${format(updatedAt, 'EEE d MMM, HH:mm')}` : 'Not synced yet';

  return (
    <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', justifyContent: 'space-between', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        {backTo ? (
          <Button component={RouterLink} to={backTo.to} size="small" startIcon={<ArrowBack />} sx={{ mb: 1, ml: -1 }}>
            {backTo.label}
          </Button>
        ) : null}
        <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>
          Staff Performance
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
        <Chip
          size="small"
          label={`Latest · ${periodLabel(periods?.latest)}`}
          sx={{ bgcolor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE', fontWeight: 500 }}
        />
        <Chip size="small" variant="outlined" label={`Prior · ${periodLabel(periods?.prior)}`} />
        <Chip size="small" variant="outlined" label={updated} />
        {meta?.status === 'error' ? (
          <Chip size="small" color="warning" variant="outlined" label="Last sync failed" title={meta.error ?? undefined} />
        ) : null}
        {updatesCheckedAt ? (
          <Chip size="small" variant="outlined" label={`Client updates checked ${format(updatesCheckedAt, 'EEE d MMM, HH:mm')}`} />
        ) : null}
        {updatesFailed ? <Chip size="small" color="warning" variant="outlined" label="Client update check failed" /> : null}
        {action}
      </Stack>
    </Box>
  );
};
